import { ValidatorSchema } from "@constants/validators";
import { zodResolver } from "@hookform/resolvers/zod";
import useAuthSession from "@hooks/useAuthSession";
import useFundingProfile from "@hooks/useFundingProfile";
import { useFundingProfileFormConfig } from "@hooks/useFundingProfileFormConfig";
import { useMemberSettings } from "@hooks/useMemberSettings";
import usePaymentInfo from "@hooks/usePaymentInfo";
import { Currency } from "@types";
import { MemberSettingFundingMode } from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { FundingProfileFieldValues } from "../types";
import {
  buildNameFieldsSchema,
  buildPidSchema,
  buildUniqueIdentifierFieldsSchema,
} from "../utils/personalInfoSchemaBuilders";
import { usePersonalInfoFieldWatchers } from "./usePersonalInfoFieldWatchers";

export const useEWalletFundingProfileFormController = () => {
  const { phoneMinLength, phoneMaxLength, phoneStartWith, phoneSimpleError, nameMode, currency } = useMemberSettings();
  const { fundingProfileList } = useFundingProfile();
  const { getPaymentMethodByKey } = usePaymentInfo();
  const { legalName } = useAuthSession();
  const { eWalletFormConfig } = useFundingProfileFormConfig();

  const checkIfFundingProfileExists = useCallback(
    (accountNo: string, accountType: string): boolean => {
      const foundFundingProfile = fundingProfileList?.find(
        (profile) =>
          profile.accNo === accountNo &&
          profile.type === getPaymentMethodByKey(accountType)?.type &&
          profile.key === accountType,
      );

      return !!foundFundingProfile;
    },
    [fundingProfileList, getPaymentMethodByKey],
  );

  const formSchema = useMemo(() => {
    // Base schema
    let schema: z.ZodTypeAny = z.object({
      accountNo: ValidatorSchema.payment.eWallet.accountNo({
        minLength: phoneMinLength,
        maxLength: phoneMaxLength,
        startWith: phoneStartWith,
        simpleError: phoneSimpleError,
      }),
      accountType: ValidatorSchema.payment.eWallet.accountType,
      isDefault: ValidatorSchema.payment.eWallet.isDefault,
    });

    // Add name fields using shared utility
    const nameFields = buildNameFieldsSchema(eWalletFormConfig, nameMode, legalName);
    if (Object.keys(nameFields).length) {
      schema = (schema as z.ZodObject<any>).extend(nameFields);
    }

    // Add unique identifier fields using shared utility
    const extraFields = buildUniqueIdentifierFieldsSchema(eWalletFormConfig);
    if (Object.keys(extraFields).length) {
      schema = (schema as z.ZodObject<any>).extend(extraFields);
    }

    // Add PID schema using shared utility
    const pidSchema = buildPidSchema(eWalletFormConfig, currency as Currency);
    let baseSchema: z.ZodTypeAny = schema;
    if (pidSchema) baseSchema = baseSchema.and(pidSchema);

    return baseSchema.refine((data) => !checkIfFundingProfileExists(data.accountNo, data.accountType), {
      message: `${MemberSettingFundingMode.EWalletOnly} already added`,
      path: ["accountNo"],
    });
  }, [
    phoneMinLength,
    phoneMaxLength,
    phoneStartWith,
    phoneSimpleError,
    nameMode,
    legalName,
    checkIfFundingProfileExists,
    currency,
    eWalletFormConfig,
  ]);

  const form = useForm<FundingProfileFieldValues>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountNo: "",
      accountType: "",
      accountDescription: "",
      isDefault: true,
      firstName: "",
      lastName: "",
      accountName: "",
      pan: "",
      cpf: "",
      curp: "",
      pidType: "",
      pidNumber: "",
    },
  });

  // Use shared personal info field watchers
  usePersonalInfoFieldWatchers(form);

  return { form, schema: formSchema };
};
